import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown, ChevronUp, Package, Truck, Camera, FileText, CreditCard, Wallet, Building2, Upload, CheckCircle2, Clock, AlertCircle, MapPin, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/api";

// ── Status config ─────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  pending:         { label: "Submitted",         color: "#f59e0b", icon: Clock },
  under_review:    { label: "Under Review",       color: "#f59e0b", icon: Clock },
  offer_made:      { label: "Offer Received",     color: "#00d4ff", icon: CheckCircle2 },
  accepted:        { label: "Offer Accepted",     color: "#22c55e", icon: Check },
  in_transit:      { label: "In Transit",         color: "#a78bfa", icon: Truck },
  store_visit:     { label: "Store Visit",        color: "#a78bfa", icon: MapPin },
  received:        { label: "Device Received",    color: "#22c55e", icon: Package },
  qc_done:         { label: "QC Done",            color: "#00d4ff", icon: CheckCircle2 },
  final_accepted:  { label: "Final Price Accepted",color: "#22c55e",icon: Check },
  docs_submitted:  { label: "Docs Submitted",     color: "#a78bfa", icon: FileText },
  payment_processing:{ label: "Payment Processing",color: "#f59e0b",icon: RefreshCw },
  paid:            { label: "Payment Done!",      color: "#22c55e", icon: CheckCircle2 },
  rejected:        { label: "Rejected",           color: "#ef4444", icon: AlertCircle },
  cancelled:       { label: "Cancelled",          color: "#ef4444", icon: AlertCircle },
};

const TIMELINE_STEPS = [
  { key: "pending",       label: "Request Submit" },
  { key: "offer_made",    label: "Initial Offer" },
  { key: "accepted",      label: "Offer Accept" },
  { key: "in_transit",    label: "Courier/Visit" },
  { key: "received",      label: "Device Received" },
  { key: "qc_done",       label: "QC Done" },
  { key: "final_accepted",label: "Final Confirm" },
  { key: "docs_submitted",label: "Docs Upload" },
  { key: "paid",          label: "Payment Done" },
];

const STATUS_ORDER = ["pending","under_review","offer_made","accepted","in_transit","store_visit","received","qc_done","final_accepted","docs_submitted","payment_processing","paid"];
function stepDone(current, stepKey) {
  const ci = STATUS_ORDER.indexOf(current);
  const si = STATUS_ORDER.indexOf(stepKey);
  return si < ci || current === stepKey;
}

const money = (v = 0) => `₹${Number(v).toLocaleString("en-IN")}`;

// ── Main component ────────────────────────────────────────────────────────
export default function SellRequestFlow({ requestId, onBack }) {
  const [req, setReq]     = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/api/sell-requests/${requestId}`);
      setReq(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [requestId]);

  if (loading) return <div className="srf-loading"><RefreshCw size={20} className="spin" /> Loading…</div>;
  if (!req) return <div className="srf-loading">Request nahi mila</div>;

  return (
    <div className="srf-root" data-testid="sell-request-flow">
      {/* Header */}
      <div className="srf-header">
        <button className="srf-back" onClick={onBack} data-testid="srf-back-btn">
          <ArrowLeft size={18} /> Back
        </button>
        <div>
          <span className="srf-req-num">#{req.request_number}</span>
          <span className="srf-device-name">{req.brand} {req.model} · {req.storage}</span>
        </div>
        <StatusBadge status={req.status} />
      </div>

      {/* Timeline */}
      <ProgressTimeline current={req.status} />

      {/* Current Action Panel */}
      <ActionPanel req={req} onRefresh={load} />

      {/* Device Details */}
      <DeviceDetails req={req} />

      {/* Full Timeline Log */}
      <TimelineLog events={req.timeline || []} />
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "#8fa5c4" };
  const Icon = cfg.icon || Clock;
  return (
    <span className="srf-badge" style={{ color: cfg.color, borderColor: `${cfg.color}35`, background: `${cfg.color}12` }}>
      <Icon size={11} /> {cfg.label}
    </span>
  );
}

// ── Progress Timeline ─────────────────────────────────────────────────────
function ProgressTimeline({ current }) {
  const isDead = ["rejected","cancelled"].includes(current);
  return (
    <div className="srf-timeline-strip">
      {TIMELINE_STEPS.map((step, i) => {
        const done = !isDead && stepDone(current, step.key);
        const active = current === step.key || (step.key === "in_transit" && ["in_transit","store_visit"].includes(current));
        return (
          <div key={step.key} className={`srf-tl-item${done ? " done" : active ? " active" : ""}`}>
            <div className="srf-tl-dot">{done && !active ? <Check size={10} /> : ""}</div>
            {i < TIMELINE_STEPS.length - 1 && <div className={`srf-tl-line${done ? " done" : ""}`} />}
            <span className="srf-tl-label">{step.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Action Panel (status-based) ───────────────────────────────────────────
function ActionPanel({ req, onRefresh }) {
  switch (req.status) {
    case "pending":
    case "under_review":
      return <WaitingPanel icon={Clock} title="Review Ho Rahi Hai" text="Hamari team aapke device ki photos review kar rahi hai. 24 ghante mein offer milega." />;

    case "offer_made":
      return <OfferPanel req={req} onRefresh={onRefresh} />;

    case "accepted":
      return req.delivery_method === "delivery"
        ? <TrackingPanel req={req} onRefresh={onRefresh} />
        : <WaitingPanel icon={MapPin} title="Store Visit Confirm" text={`DMMobile Shop par aake apna ${req.brand} ${req.model} de do. Address: Rohini West Metro Station, Shop 21-22, Rohini Sec 3, New Delhi.`} />;

    case "in_transit":
      return <InTransitPanel req={req} />;

    case "store_visit":
      return <WaitingPanel icon={MapPin} title="Visit Awaited" text="Aap jab shop aayenge, hamare team aapki device receive karenge aur QC karenge." />;

    case "received":
      return <WaitingPanel icon={Package} title="Device Received! QC Chal Rahi Hai" text="Hamari team aapki device ko carefully check kar rahi hai. Final price jald milegi." accent="#22c55e" />;

    case "qc_done":
      return <FinalPricePanel req={req} onRefresh={onRefresh} />;

    case "final_accepted":
      return <DocsUploadPanel req={req} onRefresh={onRefresh} />;

    case "docs_submitted":
      return <WaitingPanel icon={RefreshCw} title="Payment Processing" text="Documents verify ho rahe hain. Payment 1-2 din mein process hogi." accent="#a78bfa" />;

    case "payment_processing":
      return <WaitingPanel icon={RefreshCw} title="Payment On The Way" text="Payment process ho rahi hai. Jald aapke account/wallet mein aayegi." accent="#f59e0b" />;

    case "paid":
      return <PaidPanel req={req} />;

    case "rejected":
      return <WaitingPanel icon={AlertCircle} title="Offer Reject Kiya" text="Aapne offer reject kiya. Naya sell request submit kar sakte ho." accent="#ef4444" />;

    case "cancelled":
      return <WaitingPanel icon={AlertCircle} title="Request Cancel" text="Yeh request cancel ho gayi." accent="#ef4444" />;

    default:
      return null;
  }
}

// ── Waiting Panel ─────────────────────────────────────────────────────────
function WaitingPanel({ icon: Icon, title, text, accent = "#00d4ff" }) {
  return (
    <div className="srf-action-card" data-testid="srf-waiting-panel">
      <div className="srf-action-icon" style={{ background: `${accent}15`, borderColor: `${accent}30` }}>
        <Icon size={22} style={{ color: accent }} />
      </div>
      <h3 style={{ color: accent }}>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

// ── Offer Accept Panel ────────────────────────────────────────────────────
function OfferPanel({ req, onRefresh }) {
  const [delivery, setDelivery] = useState("");
  const [loading, setLoading] = useState(false);

  const respond = async (accept) => {
    if (accept && !delivery) { toast.error("Delivery method choose karo"); return; }
    setLoading(true);
    try {
      await api.post(`/api/sell-requests/${req.id}/respond`, { accept, delivery_method: delivery });
      toast.success(accept ? "Offer accept kar liya!" : "Offer reject kar diya");
      onRefresh();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="srf-action-card" data-testid="srf-offer-panel">
      <div className="srf-offer-amount">
        <span>Initial Offer</span>
        <strong>{money(req.offer_price)}</strong>
        {req.admin_notes && <small>{req.admin_notes}</small>}
      </div>

      <div className="srf-field-group">
        <p className="srf-field-label">Device bhejne ka tarika chuno:</p>
        <div className="srf-delivery-options">
          <label className={`srf-delivery-opt${delivery === "delivery" ? " selected" : ""}`} data-testid="delivery-option-courier">
            <input type="radio" name="delivery" value="delivery" onChange={() => setDelivery("delivery")} />
            <Truck size={18} />
            <div><b>Courier Bhejo</b><small>Tracking ID add karni hogi</small></div>
          </label>
          <label className={`srf-delivery-opt${delivery === "store_visit" ? " selected" : ""}`} data-testid="delivery-option-store">
            <input type="radio" name="delivery" value="store_visit" onChange={() => setDelivery("store_visit")} />
            <MapPin size={18} />
            <div><b>Shop Pe Aao</b><small>Rohini West Metro Stn, Shop 21-22, Rohini Sec 3, New Delhi</small></div>
          </label>
        </div>
      </div>

      <div className="srf-action-btns">
        <button className="srf-btn-accept" onClick={() => respond(true)} disabled={loading || !delivery} data-testid="accept-offer-btn">
          {loading ? <RefreshCw size={14} className="spin" /> : <Check size={14} />}
          Accept Karo
        </button>
        <button className="srf-btn-reject" onClick={() => respond(false)} disabled={loading} data-testid="reject-offer-btn">
          Reject Karo
        </button>
      </div>
    </div>
  );
}

// ── Tracking Input Panel ──────────────────────────────────────────────────
function TrackingPanel({ req, onRefresh }) {
  const [trackingId, setTrackingId] = useState(req.tracking_id || "");
  const [courier, setCourier] = useState(req.courier || "");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!trackingId.trim()) { toast.error("Tracking ID dalo"); return; }
    setLoading(true);
    try {
      await api.post(`/api/sell-requests/${req.id}/tracking`, { tracking_id: trackingId, courier });
      toast.success("Tracking ID add ho gayi!");
      onRefresh();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="srf-action-card" data-testid="srf-tracking-panel">
      <div className="srf-action-icon" style={{ background: "#a78bfa15", borderColor: "#a78bfa30" }}>
        <Truck size={22} style={{ color: "#a78bfa" }} />
      </div>
      <h3>Courier Tracking Add Karo</h3>
      <p>Device ship karo aur tracking ID yahan dalo taaki hum monitor kar sakein.</p>
      <div className="srf-form-fields">
        <label className="srf-field">
          <span>Courier Service</span>
          <input value={courier} onChange={e => setCourier(e.target.value)} placeholder="e.g. DTDC, Delhivery, Speed Post" data-testid="courier-name-input" />
        </label>
        <label className="srf-field">
          <span>Tracking ID *</span>
          <input value={trackingId} onChange={e => setTrackingId(e.target.value)} placeholder="e.g. 1234567890" data-testid="tracking-id-input" />
        </label>
      </div>
      <button className="srf-btn-primary" onClick={submit} disabled={loading} data-testid="submit-tracking-btn">
        {loading ? <RefreshCw size={14} className="spin" /> : <Truck size={14} />}
        Tracking Submit Karo
      </button>
    </div>
  );
}

// ── In Transit Info ───────────────────────────────────────────────────────
function InTransitPanel({ req }) {
  return (
    <div className="srf-action-card" data-testid="srf-in-transit-panel">
      <div className="srf-action-icon" style={{ background: "#a78bfa15", borderColor: "#a78bfa30" }}>
        <Truck size={22} style={{ color: "#a78bfa" }} />
      </div>
      <h3 style={{ color: "#a78bfa" }}>Device On The Way!</h3>
      <div className="srf-tracking-info">
        <div><span>Courier</span><strong>{req.courier || "—"}</strong></div>
        <div><span>Tracking ID</span><strong>{req.tracking_id || "—"}</strong></div>
      </div>
      <p style={{ fontSize: 12, color: "#6b7f9e", marginTop: 8 }}>Device receive hone pe hamari team QC karegi aur final price bhejegi.</p>
    </div>
  );
}

// ── Final Price Accept Panel (qc_done) ──────────────────────────────────
// Payment details are collected AFTER docs upload — this panel just confirms price
function FinalPricePanel({ req, onRefresh }) {
  const [loading, setLoading] = useState(false);

  const accept = async () => {
    setLoading(true);
    try {
      await api.post(`/api/sell-requests/${req.id}/accept-final`);
      toast.success("Final price confirm! Ab documents upload karo.");
      onRefresh();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const reject = async () => {
    setLoading(true);
    try {
      await api.post(`/api/sell-requests/${req.id}/respond`, { accept: false });
      toast.info("QC offer reject kar diya.");
      onRefresh();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="srf-action-card" data-testid="srf-final-price-panel">
      <div className="srf-offer-amount final">
        <span>QC Ke Baad Final Price</span>
        <strong style={{ color: "#22c55e" }}>{money(req.final_price)}</strong>
        {req.qc_notes && <small className="qc-note">{req.qc_notes}</small>}
      </div>
      <p style={{ fontSize: 12, color: "#8fa5c4", margin: "4px 0 0" }}>
        Accept karne ke baad aapko <b>Bill + Aadhaar upload</b> karna hoga aur <b>payment details</b> deni hongi.
      </p>
      <div className="srf-action-btns">
        <button className="srf-btn-accept" onClick={accept} disabled={loading} data-testid="confirm-final-price-btn">
          {loading ? <RefreshCw size={14} className="spin" /> : <Check size={14} />}
          Accept Karo → Docs Upload
        </button>
        <button className="srf-btn-reject" onClick={reject} disabled={loading} data-testid="reject-final-price-btn">
          Reject Karo
        </button>
      </div>
    </div>
  );
}

// ── Documents + Payment Details Panel (final_accepted) — 2-step ─────────
function DocsUploadPanel({ req, onRefresh }) {
  const [step, setStep] = useState(1);
  // Step 1 — docs
  const [bill, setBill]       = useState(null);
  const [aadhaar, setAadhaar] = useState(null);
  // Step 2 — payment
  const [payType, setPayType] = useState("bank");
  const [bankName, setBankName]   = useState("");
  const [bankAcc, setBankAcc]     = useState("");
  const [bankIfsc, setBankIfsc]   = useState("");
  const [upiId, setUpiId]         = useState("");
  const [showQR, setShowQR]       = useState(false);
  const [loading, setLoading]     = useState(false);
  const billRef    = useRef();
  const aadhaarRef = useRef();

  const goToStep2 = () => {
    if (!bill || !aadhaar) { toast.error("Bill aur Aadhaar dono upload karo"); return; }
    setStep(2);
  };

  const submit = async () => {
    if (payType === "bank" && (!bankAcc.trim() || !bankIfsc.trim())) {
      toast.error("Account number aur IFSC dono bharo"); return;
    }
    if ((payType === "upi" || payType === "upi_qr") && !upiId.trim()) {
      toast.error("UPI ID daalo"); return;
    }
    setLoading(true);
    const fd = new FormData();
    fd.append("bill", bill);
    fd.append("aadhaar", aadhaar);
    fd.append("payment_type", payType);
    fd.append("bank_account_name", bankName);
    fd.append("bank_account_number", bankAcc);
    fd.append("bank_ifsc", bankIfsc);
    fd.append("upi_id", upiId);
    try {
      await api.post(`/api/sell-requests/${req.id}/upload-docs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sab kuch submit ho gaya! Payment jald process hogi.");
      onRefresh();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const qrUrl = upiId.trim()
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`upi://pay?pa=${upiId.trim()}&pn=Customer&tn=DMMobile+Sell+Payment+${money(req.final_price)}`)}`
    : null;

  return (
    <div className="srf-action-card" data-testid="srf-docs-panel">
      {/* Step indicator */}
      <div className="srf-step-indicator" data-testid="srf-step-indicator">
        <div className={`srf-step-dot${step >= 1 ? " active" : ""}${step > 1 ? " done" : ""}`}>
          {step > 1 ? <Check size={12} /> : "1"}
        </div>
        <div className={`srf-step-line${step > 1 ? " done" : ""}`} />
        <div className={`srf-step-dot${step >= 2 ? " active" : ""}`}>2</div>
        <div style={{ fontSize: 11, color: "#6b7f9e", marginLeft: 8, flex: 1 }}>
          {step === 1 ? "Step 1: Documents Upload karo" : "Step 2: Payment Details dalo"}
        </div>
      </div>

      {/* ── STEP 1: Document Upload ── */}
      {step === 1 && (
        <>
          <div className="srf-action-icon" style={{ background: "#00d4ff15", borderColor: "#00d4ff30" }}>
            <FileText size={22} style={{ color: "#00d4ff" }} />
          </div>
          <h3>Documents Upload Karo</h3>
          <p>iPhone ka <b>original bill</b> aur aapka <b>Aadhaar card</b> upload karo.</p>

          <div className="srf-doc-upload-grid">
            <div className="srf-doc-slot" onClick={() => billRef.current?.click()} data-testid="bill-upload-slot">
              <input ref={billRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={e => setBill(e.target.files[0])} />
              {bill
                ? <><CheckCircle2 size={20} color="#22c55e" /><span style={{ color: "#22c55e", fontSize: 12 }}>Bill: {bill.name}</span></>
                : <><Upload size={20} /><span>iPhone Bill / Invoice</span><small>JPG, PNG, PDF</small></>
              }
            </div>
            <div className="srf-doc-slot" onClick={() => aadhaarRef.current?.click()} data-testid="aadhaar-upload-slot">
              <input ref={aadhaarRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={e => setAadhaar(e.target.files[0])} />
              {aadhaar
                ? <><CheckCircle2 size={20} color="#22c55e" /><span style={{ color: "#22c55e", fontSize: 12 }}>Aadhaar: {aadhaar.name}</span></>
                : <><Upload size={20} /><span>Aadhaar Card</span><small>Front side JPG / PNG</small></>
              }
            </div>
          </div>

          <div className="srf-warning">
            <AlertCircle size={13} /> Documents ki photo clear honi chahiye. Blurry photos reject ho sakti hain.
          </div>

          <button className="srf-btn-primary" onClick={goToStep2} disabled={!bill || !aadhaar} data-testid="go-to-step2-btn">
            <Check size={14} /> Documents Select → Payment Details
          </button>
        </>
      )}

      {/* ── STEP 2: Payment Details ── */}
      {step === 2 && (
        <>
          <div className="srf-action-icon" style={{ background: "#22c55e15", borderColor: "#22c55e30" }}>
            <CreditCard size={22} style={{ color: "#22c55e" }} />
          </div>
          <h3>Payment Details Dalo</h3>
          <p style={{ fontSize: 12, color: "#8fa5c4" }}>
            Final amount <strong style={{ color: "#22c55e" }}>{money(req.final_price)}</strong> aapke account mein transfer hoga.
          </p>

          {/* Payment type selection */}
          <div className="srf-delivery-options" style={{ marginBottom: 12 }}>
            <label className={`srf-delivery-opt${payType === "bank" ? " selected" : ""}`} data-testid="pay-type-bank">
              <input type="radio" name="paytype" value="bank" checked={payType === "bank"} onChange={() => setPayType("bank")} />
              <Building2 size={18} />
              <div><b>Bank Account Transfer</b><small>1-2 din mein NEFT/IMPS</small></div>
            </label>
            <label className={`srf-delivery-opt${payType === "upi" ? " selected" : ""}`} data-testid="pay-type-upi">
              <input type="radio" name="paytype" value="upi" checked={payType === "upi"} onChange={() => setPayType("upi")} />
              <Wallet size={18} />
              <div><b>UPI ID</b><small>Instant — aapka UPI ID dalo</small></div>
            </label>
            <label className={`srf-delivery-opt${payType === "upi_qr" ? " selected" : ""}`} data-testid="pay-type-upi-qr">
              <input type="radio" name="paytype" value="upi_qr" checked={payType === "upi_qr"} onChange={() => setPayType("upi_qr")} />
              <Camera size={18} />
              <div><b>UPI QR Code</b><small>QR generate hoga — humse scan karwao</small></div>
            </label>
          </div>

          {/* Bank fields */}
          {payType === "bank" && (
            <div className="srf-form-fields">
              <label className="srf-field">
                <span>Account Holder Name</span>
                <input value={bankName} onChange={e => setBankName(e.target.value)} placeholder="Aapka naam" data-testid="bank-name-input" />
              </label>
              <label className="srf-field">
                <span>Account Number *</span>
                <input value={bankAcc} onChange={e => setBankAcc(e.target.value.replace(/\D/g, ""))} placeholder="Bank account number" data-testid="bank-account-input" />
              </label>
              <label className="srf-field">
                <span>IFSC Code *</span>
                <input value={bankIfsc} onChange={e => setBankIfsc(e.target.value.toUpperCase())} placeholder="e.g. SBIN0001234" data-testid="bank-ifsc-input" />
              </label>
            </div>
          )}

          {/* UPI ID field */}
          {(payType === "upi" || payType === "upi_qr") && (
            <div className="srf-form-fields">
              <label className="srf-field">
                <span>Aapka UPI ID *</span>
                <input value={upiId} onChange={e => setUpiId(e.target.value.trim())} placeholder="e.g. 9876543210@paytm" data-testid="upi-id-input" />
              </label>
            </div>
          )}

          {/* QR Code preview */}
          {payType === "upi_qr" && upiId.trim() && (
            <div className="srf-qr-preview" data-testid="upi-qr-preview">
              <div style={{ fontSize: 12, color: "#8fa5c4", marginBottom: 8 }}>
                Yeh QR code shop pe show karo — hum scan karke payment karenge:
              </div>
              <img src={qrUrl} alt="UPI QR Code" style={{ width: 160, height: 160, borderRadius: 10, border: "2px solid rgba(34,197,94,0.3)", background: "#fff", padding: 4 }} />
              <small style={{ display: "block", color: "#22c55e", fontSize: 11, marginTop: 6 }}>UPI: {upiId}</small>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button className="srf-btn-reject" onClick={() => setStep(1)} style={{ flex: "0 0 auto" }} data-testid="back-to-step1-btn">
              ← Wapas
            </button>
            <button className="srf-btn-primary" onClick={submit} disabled={loading} data-testid="submit-docs-btn" style={{ flex: 1 }}>
              {loading ? <RefreshCw size={14} className="spin" /> : <Check size={14} />}
              {loading ? "Submitting…" : "Submit Karo — Done!"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Payment Done Panel ────────────────────────────────────────────────────
function PaidPanel({ req }) {
  const method = req.payment_method === "bank" ? "Bank Account" : "DMMobile Wallet";
  return (
    <div className="srf-action-card paid" data-testid="srf-paid-panel">
      <div className="srf-action-icon" style={{ background: "#22c55e15", borderColor: "#22c55e30" }}>
        <CheckCircle2 size={28} style={{ color: "#22c55e" }} />
      </div>
      <h3 style={{ color: "#22c55e" }}>Payment Complete! </h3>
      <div className="srf-paid-info">
        <div><span>Amount</span><strong>{money(req.paid_amount || req.final_price)}</strong></div>
        <div><span>Payment Method</span><strong>{method}</strong></div>
        {req.paid_note && <div><span>Note</span><strong>{req.paid_note}</strong></div>}
      </div>
      {req.payment_proof_url && (
        <a href={req.payment_proof_url} target="_blank" rel="noreferrer" className="srf-proof-link" data-testid="payment-proof-link">
          <CreditCard size={14} /> Payment Proof Dekho
        </a>
      )}
      <Link to="/account" className="srf-btn-primary" style={{ marginTop: 12, textDecoration: "none", justifyContent: "center" }}>
        <Wallet size={14} /> Wallet Dekho
      </Link>
    </div>
  );
}

// ── Device Details ─────────────────────────────────────────────────────────
function DeviceDetails({ req }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="srf-section">
      <button className="srf-section-toggle" onClick={() => setOpen(o => !o)}>
        <span><Camera size={14} /> Device Details</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="srf-device-grid">
          <div><span>Brand</span><b>{req.brand}</b></div>
          <div><span>Model</span><b>{req.model}</b></div>
          <div><span>Storage</span><b>{req.storage}</b></div>
          <div><span>Color</span><b>{req.color}</b></div>
          <div><span>Condition</span><b>{req.condition}</b></div>
          {req.issues?.length > 0 && <div style={{ gridColumn: "1/-1" }}><span>Issues</span><b>{req.issues.join(", ")}</b></div>}
          {req.description && <div style={{ gridColumn: "1/-1" }}><span>Description</span><b>{req.description}</b></div>}
          {req.photos?.length > 0 && (
            <div style={{ gridColumn: "1/-1" }}>
              <span>Photos</span>
              <div className="srf-photos">
                {req.photos.map((url, i) => (
                  <img key={i} src={url} alt={`Photo ${i+1}`} onError={e => { e.currentTarget.style.display = "none"; }} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Timeline Log ───────────────────────────────────────────────────────────
function TimelineLog({ events }) {
  const [open, setOpen] = useState(false);
  if (!events.length) return null;
  return (
    <div className="srf-section">
      <button className="srf-section-toggle" onClick={() => setOpen(o => !o)}>
        <span><Clock size={14} /> Activity Log ({events.length})</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="srf-timeline-log">
          {[...events].reverse().map((ev, i) => (
            <div key={i} className="srf-log-item">
              <div className="srf-log-dot" />
              <div>
                <b>{ev.label}</b>
                <small>{new Date(ev.at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</small>
                {ev.by && <span className="srf-log-by"> · {ev.by}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
